import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import { Vector3 } from "three";
import {
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { TDesignObjectView } from "../../../viewer/viewer";
import { IFC_OBJECT_SELECTED_EVENT } from "../ToolBar/IfcTabPanel";

type Coord = { x: number; y: number; z: number };

/**
 * IFC 配置モック: 配置オブジェクト (プレースホルダー + 配置済み) の操作を制御する。
 * - IFC_PLACEMENT モード中ずっと操作ハンドラを有効化 (配置済みオブジェクトの
 *   クリック選択 / ドラッグも扱う)。配置済み選択時は IFC_OBJECT_SELECTED_EVENT
 *   をドロワー (一覧) に通知してハイライトさせる。
 * - 新規オブジェクト選択中 (selectedIfcObjectId) は実 IFC をプレースホルダー表示。
 * - 移動 / 回転 / スケールは overlay gizmo (IfcObjectGizmo) とマウス操作で行う。
 * - 多方向ビュー (上下前後左右) でカメラを切り替え、座標 (read-only) をライブ表示。
 */
export const IfcPlacementPanel = () => {
  const { t } = useTranslation();
  const {
    viewer,
    currentViewerMode,
    selectedIfcObjectId,
    selectedIfcObjectUrl,
  } = useContext(ProjectDetailContext);

  const [coord, setCoord] = useState<Coord | null>(null);

  // IFC モード中は操作ハンドラを常時有効化する (配置済みオブジェクトの選択用)。
  useEffect(() => {
    if (!viewer) return;
    if (currentViewerMode !== EViewerMode.IFC_PLACEMENT) return;

    // ドラッグ中の座標更新は requestAnimationFrame でまとめ、
    // ポインタ移動ごとに React 再レンダリングが走らないようにする。
    let rafId: number | null = null;
    let latest: Coord | null = null;

    viewer.enableDesignObjectInteraction(
      (pos: Vector3) => {
        latest = { x: pos.x, y: pos.y, z: pos.z };
        if (rafId != null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (latest) setCoord(latest);
        });
      },
      (id: string | null) => {
        // 配置済みオブジェクトの選択をドロワー (一覧) に通知してハイライト。
        document.dispatchEvent(
          new CustomEvent(IFC_OBJECT_SELECTED_EVENT, { detail: { id } }),
        );
      },
    );

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      viewer.disableDesignObjectInteraction();
      setCoord(null);
    };
  }, [viewer, currentViewerMode]);

  // 配置は「リストからドラッグ&ドロップ」で行う (ProjectARView の onDrop が
  // ドロップ地点へプレースホルダーを出す)。ここではクリックだけでは配置せず、
  // 選択解除 (null) 時にプレースホルダーを消すだけにする。
  useEffect(() => {
    if (!viewer) return;
    if (currentViewerMode !== EViewerMode.IFC_PLACEMENT) return;
    if (selectedIfcObjectId == null) {
      viewer.hideDesignPlaceholder();
      setCoord(null);
    }
  }, [viewer, currentViewerMode, selectedIfcObjectId, selectedIfcObjectUrl]);

  if (!coord || !viewer) return null;

  return <></>;
};
